/**
 * The organization's Roof Quote Widget pricing, which Direct Mail quotes from.
 *
 * The server recomputes and freezes the real price whenever a measurement is
 * submitted (marketing/services/direct_mail/pricing.py), and that frozen number
 * is what gets printed. The same arithmetic is mirrored here purely so the
 * measurer watches the price move while typing — a quick-measure screen that
 * shows nothing until it has round-tripped is a screen nobody sanity-checks.
 * Treat every number from this module as a preview; the saved pin is the truth.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { PitchBucket } from '../../types/directMail';

export interface RoofQuoteTier {
  id: number;
  rank: 'good' | 'better' | 'best';
  name: string;
  description: string;
  base_price_per_square: string;
  waste_factor: string;
  pitch_multiplier_low: string;
  pitch_multiplier_med: string;
  pitch_multiplier_steep: string;
}

export interface RoofQuoteWidgetConfig {
  id: number;
  name: string;
  price_range_pct: string;
  show_price_range: boolean;
  tiers: RoofQuoteTier[];
}

/** Where a user goes to enter a price per square before any of this works. */
export const ROOF_QUOTE_SETUP_PATH = '/marketing/roof-quote-widget';

export function useRoofQuoteWidget() {
  return useQuery({
    queryKey: ['direct-mail', 'roof-quote-widget'],
    queryFn: async () => {
      const response = await apiClient.get<RoofQuoteWidgetConfig>(
        '/api/marketing/roof-quote-widgets/mine/',
      );
      if (response.data == null) {
        throw new Error(`Could not load your Roof Quote pricing (HTTP ${response.status})`);
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Rise over a 12" run, the way a roofer writes it, as the backend parses it. */
const PITCH_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*(?:[/:]\s*(\d+(?:\.\d+)?))?\s*$/;

export const PITCH_OPTIONS = [
  '2/12',
  '3/12',
  '4/12',
  '5/12',
  '6/12',
  '7/12',
  '8/12',
  '9/12',
  '10/12',
  '12/12',
];

export const PITCH_BUCKET_LABELS: Record<PitchBucket, string> = {
  low: 'Low pitch',
  med: 'Medium pitch',
  steep: 'Steep pitch',
};

export function pitchDegrees(pitch: string): number | null {
  const match = PITCH_PATTERN.exec(pitch ?? '');
  if (!match) return null;
  const rise = Number(match[1]);
  const run = Number(match[2] ?? 12);
  if (!Number.isFinite(rise) || !Number.isFinite(run) || run <= 0) return null;
  return (Math.atan(rise / run) * 180) / Math.PI;
}

/** Mirrors pitch_bucket_from_degrees: an unreadable pitch prices as medium. */
export function bucketForPitch(pitch: string): PitchBucket {
  const degrees = pitchDegrees(pitch);
  if (degrees === null) return 'med';
  if (degrees < 18) return 'low';
  if (degrees <= 34) return 'med';
  return 'steep';
}

function toNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function tierMultiplier(tier: RoofQuoteTier, bucket: PitchBucket): number {
  if (bucket === 'low') return toNumber(tier.pitch_multiplier_low);
  if (bucket === 'steep') return toNumber(tier.pitch_multiplier_steep);
  return toNumber(tier.pitch_multiplier_med);
}

export interface QuotePreview {
  low: number;
  high: number;
  squares: number;
  squaresWithWaste: number;
  bucket: PitchBucket;
}

export function previewQuote(args: {
  tier: RoofQuoteTier | null | undefined;
  rangePct: string | number;
  roofAreaSqft: number;
  bucket: PitchBucket;
  /** A per-roof override; zero is valid, and only an unset value uses the tier. */
  wasteFactor?: number | null;
}): QuotePreview | null {
  const { tier, rangePct, roofAreaSqft, bucket, wasteFactor } = args;
  if (!tier || !Number.isFinite(roofAreaSqft) || roofAreaSqft <= 0) return null;

  const waste = wasteFactor != null && Number.isFinite(wasteFactor) && wasteFactor >= 0
    ? wasteFactor
    : toNumber(tier.waste_factor);
  const squares = roofAreaSqft / 100;
  const squaresWithWaste = squares * (1 + waste);
  const base = squaresWithWaste * toNumber(tier.base_price_per_square) * tierMultiplier(tier, bucket);
  const range = toNumber(rangePct);

  return {
    low: base * (1 - range),
    high: base * (1 + range),
    squares,
    squaresWithWaste,
    bucket,
  };
}

/** Resolution order the backend uses: explicit tier, then campaign, then "better". */
export function resolveTier(
  tiers: RoofQuoteTier[] | undefined,
  preferredId?: number | null,
): RoofQuoteTier | null {
  if (!tiers || tiers.length === 0) return null;
  if (preferredId) {
    const explicit = tiers.find((tier) => tier.id === preferredId);
    if (explicit) return explicit;
  }
  return tiers.find((tier) => tier.rank === 'better') ?? tiers[0];
}
